import React from 'react';
import {
    Box,
    VStack,
    Text,
    Heading,
    Container,
    SimpleGrid,
    List,
    ListItem,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Icon,
    Flex,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    useColorModeValue,
    Image,
    Badge,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
    FaGlobe, 
    FaLock, 
    FaBolt, 
    FaUserShield, 
    FaChartLine, 
    FaGamepad,
    FaCoins,
    FaRocket,
    FaUsers,
    FaCheckCircle,
    FaClock,
    FaTrophy,
    FaShieldAlt,
    FaDice,
    FaChartBar,
    FaHandshake,
} from 'react-icons/fa';
import {
    GiPirateSkull,
    GiChest,
    GiOpenChest,
    GiShipWheel,
    GiPalmTree,
    GiPirateFlag,
    GiIsland,
    GiSkullCrossedBones,
    GiPirateCaptain,
    GiCannon,
    GiPirateHat,
    GiTreasureMap,
    GiSwordman
} from 'react-icons/gi';

// Brand Colors
const brandColors = {
    primary: "#8247E5",    // Purple
    secondary: "#00E676",  // Green
    accent1: "#FF3366",    // Pink
    accent2: "#00BCD4",    // Cyan
    accent3: "#FFE66D",    // Yellow
    gold: "#FFD700",       // Gold
    darkGold: "#E6C200",   // Dark Gold
    lightGold: "#FFDF00"   // Light Gold
};

const MotionBox = motion(Box);

const Card = ({ children, color, height }) => (
    <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        whileHover={{ y: -5 }}
        height={height}
    >
        <Box
            bg="rgba(13, 16, 25, 0.9)"
            borderRadius="xl"
            border="1px solid"
            borderColor={color}
            p={6}
            position="relative"
            overflow="hidden"
            boxShadow={`0 4px 20px ${color}25`}
            _hover={{
                boxShadow: `0 8px 30px ${color}40`,
            }}
            transition="all 0.3s ease"
            height="100%"
        >
            {children}
        </Box>
    </MotionBox>
);

const FeatureCard = ({ title, description, icon, color, emoji }) => (
    <Card color={color} height="200px">
        <VStack 
            spacing={3} 
            align="center" 
            textAlign="center"
            height="100%"
            justify="center"
        >
            <Flex
                w="60px"
                h="60px"
                bg={color}
                borderRadius="full"
                align="center"
                justify="center"
                mb={1}
            >
                <Icon as={icon} w={6} h={6} color="white" />
            </Flex>
            <Text fontSize="lg" fontWeight="bold" color={color}>
                {emoji} {title}
            </Text>
            <Text color="whiteAlpha.800" noOfLines={2} fontSize="sm">{description}</Text>
        </VStack>
    </Card>
);

const StatBox = ({ title, value, description, icon, color }) => (
    <Card color={color}>
        <VStack spacing={3} align="center">
            <Icon as={icon} w={6} h={6} color={color} />
            <Stat textAlign="center">
                <StatLabel fontSize="lg" color="whiteAlpha.800">{title}</StatLabel>
                <StatNumber fontSize="3xl" fontWeight="bold" color={color}>
                    {value}
                </StatNumber>
                <StatHelpText color="whiteAlpha.700">{description}</StatHelpText>
            </Stat>
        </VStack>
    </Card>
);

const ComparisonRow = ({ feature, traditional, palmheist, icon }) => (
    <Tr>
        <Td>
            <Flex align="center" gap={2}>
                <Icon as={icon} color={brandColors.gold} />
                <Text>{feature}</Text>
            </Flex>
        </Td>
        <Td color="whiteAlpha.700">{traditional}</Td>
        <Td>
            <Text color={brandColors.secondary} fontWeight="bold">
                {palmheist}
            </Text>
        </Td>
    </Tr>
);

const WhitePaper = () => {
    return (
        <Box
            minH="100vh"
            bg="#0D1019"
            color="white"
            pt={20}
            pb={20}
            position="relative"
        >
            {/* Decorative Pirate Icons in Background */}
            <Box position="absolute" top="120px" left="5%" opacity="0.3" zIndex="0">
                <Icon as={GiPirateSkull} color={brandColors.gold} boxSize={12} />
            </Box>
            <Box position="absolute" bottom="15%" right="5%" opacity="0.3" zIndex="0">
                <Icon as={GiOpenChest} color={brandColors.gold} boxSize={12} />
            </Box>
            <Box position="absolute" top="30%" right="8%" opacity="0.3" zIndex="0">
                <Icon as={GiShipWheel} color={brandColors.gold} boxSize={12} />
            </Box>
            <Box position="absolute" bottom="30%" left="8%" opacity="0.3" zIndex="0">
                <Icon as={GiPalmTree} color={brandColors.gold} boxSize={12} />
            </Box>
            <Box position="absolute" top="50%" right="15%" opacity="0.3" zIndex="0">
                <Icon as={GiPirateFlag} color={brandColors.gold} boxSize={12} />
            </Box>
            <Box position="absolute" bottom="50%" left="15%" opacity="0.3" zIndex="0">
                <Icon as={GiTreasureMap} color={brandColors.gold} boxSize={12} />
            </Box>
            <Box position="absolute" top="70%" left="25%" opacity="0.3" zIndex="0">
                <Icon as={GiCannon} color={brandColors.gold} boxSize={12} />
            </Box>
            <Box position="absolute" bottom="70%" right="25%" opacity="0.3" zIndex="0">
                <Icon as={GiPirateCaptain} color={brandColors.gold} boxSize={12} />
            </Box>
            
            <Container maxW="container.xl" position="relative" zIndex="1">
                {/* Header Section */}
                <VStack spacing={6} mb={16} textAlign="center">
                    <Image 
                        src="/images/palm-heist-logo.png" 
                        alt="PALMHEIST" 
                        w="150px" 
                        mb={4}
                        filter="drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.2))"
                    />
                    <Heading
                        fontSize={{ base: "4xl", md: "6xl" }}
                        fontWeight="bold"
                        color={brandColors.primary}
                        letterSpacing="tight"
                    >
                        🎮 PALMHEIST
                    </Heading>
                    <Text
                        fontSize={{ base: "xl", md: "2xl" }}
                        color="whiteAlpha.900"
                        maxW="800px"
                    >
                        Revolutionizing Instant Gaming with Web3 Technology
                    </Text>
                </VStack>

                {/* Introduction Section */}
                <Card color={brandColors.primary}>
                    <VStack spacing={4} align="start">
                        <Heading size="lg" color={brandColors.primary}>
                            🚀 Introduction
                        </Heading>
                        <Text color="whiteAlpha.900" fontSize="lg" lineHeight="tall">
                            PALMHEIST represents a transformative leap in instant gaming, combining the appeal of traditional card-flipping games with the cutting-edge capabilities of blockchain technology. Built on the Solana network, PALMHEIST offers players a seamless, transparent, and rewarding gaming experience that caters to a global audience.
                        </Text>
                    </VStack>
                </Card>

                {/* Market Stats Section */}
                <Box my={16}>
                    <Heading size="lg" mb={8} textAlign="center" color={brandColors.accent2}>
                        📊 Market Statistics
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                        <StatBox
                            title="Global Market Value"
                            value="$300B+"
                            description="in 2022"
                            icon={FaChartLine}
                            color={brandColors.accent2}
                        />
                        <StatBox
                            title="Daily Players"
                            value="Millions"
                            description="Worldwide"
                            icon={FaUsers}
                            color={brandColors.accent3}
                        />
                        <StatBox
                            title="Transaction Speed"
                            value="65,000 TPS"
                            description="Solana Network"
                            icon={FaBolt}
                            color={brandColors.accent2}
                        />
                    </SimpleGrid>
                </Box>

                {/* Key Features Section */}
                <Box my={16}>
                    <Heading size="lg" mb={8} textAlign="center" color={brandColors.accent4}>
                        ⭐ Key Features
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                        <FeatureCard
                            title="Decentralized"
                            description="Fully transparent and verifiable on Solana blockchain"
                            icon={FaLock}
                            color={brandColors.primary}
                            emoji="🔐"
                        />
                        <FeatureCard
                            title="Global Access"
                            description="Play from anywhere, anytime without restrictions"
                            icon={FaGlobe}
                            color={brandColors.accent1}
                            emoji="🌍"
                        />
                        <FeatureCard
                            title="Instant Rewards"
                            description="Immediate payouts with near-zero fees"
                            icon={FaCoins}
                            color={brandColors.accent3}
                            emoji="💰"
                        />
                    </SimpleGrid>
                </Box>

                {/* Comparison Table */}
                <Box my={16}>
                    <Heading size="lg" mb={8} textAlign="center" color={brandColors.accent5}>
                        🔄 Traditional vs PALMHEIST
                    </Heading>
                    <Card color={brandColors.accent5}>
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th color="whiteAlpha.900">Feature</Th>
                                    <Th color="whiteAlpha.900">Traditional</Th>
                                    <Th color="whiteAlpha.900">PALMHEIST</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                <ComparisonRow
                                    feature="Control"
                                    traditional="Government/Corporate Monopoly"
                                    palmheist="Decentralized via Blockchain"
                                    icon={FaUserShield}
                                />
                                <ComparisonRow
                                    feature="Fairness"
                                    traditional="Cannot Be Verified"
                                    palmheist="Fully Transparent (Solrand VRF)"
                                    icon={FaCheckCircle}
                                />
                                <ComparisonRow
                                    feature="Availability"
                                    traditional="Region-Limited"
                                    palmheist="Global Access"
                                    icon={FaGlobe}
                                />
                                <ComparisonRow
                                    feature="Payouts"
                                    traditional="Delayed and Taxed"
                                    palmheist="Instant and Tax-Free"
                                    icon={FaCoins}
                                />
                            </Tbody>
                        </Table>
                    </Card>
                </Box>

                {/* Future Vision Section */}
                <Box my={16}>
                    <Heading size="lg" mb={8} textAlign="center" color={brandColors.secondary}>
                        🔮 Future Vision
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                        <Card color={brandColors.secondary}>
                            <VStack spacing={4} align="start">
                                <Flex
                                    w="50px"
                                    h="50px"
                                    bg={brandColors.secondary}
                                    borderRadius="full"
                                    align="center"
                                    justify="center"
                                    mb={2}
                                >
                                    <Icon as={FaRocket} w={6} h={6} color="white" />
                                </Flex>
                                <Text fontSize="xl" fontWeight="bold" color={brandColors.secondary}>
                                    🎯 Scalability
                                </Text>
                                <Text color="whiteAlpha.800">
                                    Built on Solana's high-speed blockchain to handle thousands of simultaneous players with ease.
                                </Text>
                            </VStack>
                        </Card>
                        <Card color={brandColors.accent1}>
                            <VStack spacing={4} align="start">
                                <Flex
                                    w="50px"
                                    h="50px"
                                    bg={brandColors.accent1}
                                    borderRadius="full"
                                    align="center"
                                    justify="center"
                                    mb={2}
                                >
                                    <Icon as={FaHandshake} w={6} h={6} color="white" />
                                </Flex>
                                <Text fontSize="xl" fontWeight="bold" color={brandColors.accent1}>
                                    🤝 Community Governance
                                </Text>
                                <Text color="whiteAlpha.800">
                                    Future integration with DAO ensures Genesis NFT holders can shape the game's evolution.
                                </Text>
                            </VStack>
                        </Card>
                    </SimpleGrid>
                </Box>

                {/* Conclusion Section */}
                <Box my={16}>
                    <Card color={brandColors.accent3}>
                        <VStack spacing={6} align="center" textAlign="center">
                            <Heading size="lg" color={brandColors.accent3}>
                                🎮 Join the Revolution
                            </Heading>
                            <Text fontSize="lg" color="whiteAlpha.900">
                                The future of instant gaming is here, and it's bold, transparent, and decentralized. PALMHEIST is ready to lead the charge.
                            </Text>
                            <Badge
                                px={4}
                                py={2}
                                borderRadius="full"
                                bg={brandColors.accent3}
                                color="black"
                                fontSize="md"
                            >
                                🚀 Experience the Future of Gaming
                            </Badge>
                        </VStack>
                    </Card>
                </Box>
            </Container>
        </Box>
    );
};

export default WhitePaper;
